import { useEffect, useRef } from 'react';
import customAxios from 'routes/Pages/util/Api';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';
import authHeader from 'services/auth/jwt/auth-header';

const useAutoSave = (siteAssessmentId, formData, isFormCreated, mainTableData) => {
  const debounceTimers = useRef({});
  const isInitialLoad = useRef(true);

  const sectionConfig = {
    SITE_INFO: {
      endpoint: '/SiteDetails/EditSiteDetailsInfo',
      tableType: 'detail',
      fields: [
        'isSignalStrengthGood',
        'descriptionType',
        'standingType',
        'urea',
        'timberQuality',
        'access',
        'remainingTreesPerHa',
      ],
    },

    HAZARDS: {
      endpoint: '/SiteDetails/EditSiteDetailsHazards',
      tableType: 'detail',
      fields: ['powerlines', 'groundSlope', 'groundCondition', 'streamsDrains', 'others'],
    },

    SITE_HARVESTING: {
      endpoint: '/SiteDetails/EditSiteDetailsHarvesting',
      tableType: 'detail',
      fields: [
        'treeHeight',
        'volumePerWeekHarvestor',
        'volumePerWeekForwarder',
        'extractionOver300meters',
        'tracksWheelchains',
        'diggerWorkDays',
        'chainsawWorkDays',
      ],
    },

    CHECKLIST: {
      endpoint: '/SiteDetails/EditSiteDetailsChecklist',
      tableType: 'detail',
      valueType: 'checklist',
      fields: [
        'bankAccountDetails',
        'dropPinUpdatedonWhatsApp',
        'dropPinUpdatedonGoogleMaps',
        'safetyStatement',
        'copyoffellinglicensesattached',
        'serviceItemsforMachineandMachineServicesUptodate',
        'confirmCertifiedFireExtinguisheronsite',
        'safetySignserected',
        'siteMonitoringForms',
        'googleHarvestPlancompleted',
        'jotformHarvestPlansenttoOperator',
      ],
    },

    MACHINES: {
      endpoint: '/CRM/EditCRMSiteDetails',
      tableType: 'main',
      fields: ['harvesterMachine', 'extractionMachine'],
    },

    FINALIZE: {
      endpoint: '/CRM/EditCRMSiteDetails',
      tableType: 'main',
      fields: [
        'siteNotesforOperatorsOnly',
        'siteNotesforHaulageOnly',
        'siteNotesforForestManagerOnly',
        'additionalManagementNotes',
        'finaliseAssessment',
      ],
    },
  };

  useEffect(() => {
    if (isFormCreated && isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, [isFormCreated]);

  // Helper numeric
  const numericDefaults = {
    remainingTreesPerHa: 1700,
    treeHeight: 0,
    volumePerWeekHarvestor: 0,
    volumePerWeekForwarder: 0,
  };

  const buildPayload = config => {
    const data = {};

    config.fields.forEach(field => {
      const value = formData[field];

      if (config.valueType === 'checklist') {
        data[field] = value === true || value === 'Yes' ? 'Yes' : 'No';
      } else if (numericDefaults.hasOwnProperty(field)) {
        data[field] = value ?? numericDefaults[field];
      } else {
        data[field] = value ?? '';
      }
    });

    return data;
  };

  const saveSection = async (sectionName, config) => {
    try {
      window.dispatchEvent(new CustomEvent('autosave:saving'));

      if (config.tableType === 'main') {
        const existingData = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${siteAssessmentId}`, {
          headers: authHeader(),
        });

        if (existingData.status === 200) {
          // ✅ Get the first record if response is an array
          const existing = Array.isArray(existingData.data) ? existingData.data[0] : existingData.data;

          // ✅ Merge: existing fields + overwrite with user's latest changes
          const merged = {
            ...existing,
            ref: existing.siteDetailsRef, // Ensure ref is included
            ...buildPayload(config),
          };

          const fd = new FormData();
          Object.entries(merged).forEach(([key, value]) => {
            fd.append(key, value ?? '');
          });

          await customAxios.post(config.endpoint, fd, {
            headers: {
              ...authHeader(),
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      } else {
        await customAxios.post(
          config.endpoint,
          {
            siteAssessmentID: siteAssessmentId,
            ...buildPayload(config),
          },
          { headers: authHeader() },
        );
      }

      await updateRecordModifiedDate(siteAssessmentId);

      window.dispatchEvent(new CustomEvent('autosave:saved'));
    } catch (err) {
      console.error(`❌ Autosave failed for ${sectionName}`, err);
      window.dispatchEvent(new CustomEvent('autosave:error'));
    }
  };

  useEffect(() => {
    if (!isFormCreated || !siteAssessmentId || isInitialLoad.current) return;

    Object.entries(sectionConfig).forEach(([sectionName, config]) => {
      if (debounceTimers.current[sectionName]) {
        clearTimeout(debounceTimers.current[sectionName]);
      }

      debounceTimers.current[sectionName] = setTimeout(() => {
        saveSection(sectionName, config);
      }, 1500);
    });

    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, [formData, isFormCreated, siteAssessmentId]);

  return { saveSection };
};

export default useAutoSave;
